import React, { useState, useCallback, useRef } from 'react';
import { Icons } from './Icons';
import { useTree } from '../contexts/TreeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { decodeGedcom, importGedcom } from '../utils/importGedcom';
import { runGedcomImport } from '../utils/runGedcomImport';
import { track } from '../utils/analytics';

/**
 * Import a GEDCOM file into the active tree.
 *
 * The file is parsed and previewed before anything is written, because an
 * import is the one action in this app that can add a thousand people at once
 * and has no undo. What the preview shows is what the parser actually produced
 * — counts and warnings from the real parse, not an estimate — so the decision
 * to go ahead is made against the same data that will be written.
 *
 * People are added to the tree; nothing existing is touched or matched against.
 * Merging a file into a tree that already holds the same family is a different
 * feature and a much harder one.
 */
export default function ImportGedcomModal({ onClose }) {
  const { bulkCreatePeople, batchUpdatePeople, peopleCount } = useTree();
  const { maxPeople } = useSubscription();

  const [stage, setStage] = useState('choose'); // choose | preview | running | done
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ phase: 'creating', done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const room = Math.max(maxPeople - peopleCount, 0);
  const tooManyPeople = parsed ? parsed.people.length > room : false;

  const readFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const { text, warnings: decodeWarnings } = decodeGedcom(buffer);
      const outcome = importGedcom(text);

      if (!outcome.people.length) {
        setError(
          'No people were found in this file. It may not be a GEDCOM file, or it '
          + 'may be empty.'
        );
        setStage('choose');
        return;
      }

      setParsed({ ...outcome, warnings: [...decodeWarnings, ...outcome.warnings] });
      setStage('preview');
    } catch (e) {
      setError('That file could not be read.');
      setStage('choose');
    }
  }, []);

  const start = useCallback(async () => {
    setStage('running');
    setProgress({ phase: 'creating', done: 0, total: parsed.people.length });
    const startedAt = Date.now();
    try {
      const outcome = await runGedcomImport({
        people: parsed.people,
        createPeople: (dataList) => bulkCreatePeople(dataList),
        updatePeople: (updates) => batchUpdatePeople(updates),
        onProgress: setProgress,
      });
      setResult(outcome);
      // `created`, not `total`: runGedcomImport reports a partial import in its
      // result rather than throwing, so a run that stopped halfway still
      // arrives here and would otherwise claim it imported the whole file.
      track('gedcom_import_completed', {
        people_imported: outcome.created,
        people_in_file: parsed.people.length,
        duration_ms: Date.now() - startedAt,
        stopped: outcome.stopped ? outcome.stopped.phase : 'no',
      });
    } catch (e) {
      // runGedcomImport reports a partial import in its result rather than
      // throwing, so reaching here means something unforeseen — still report
      // it as a stop rather than leaving the modal spinning.
      setResult({
        created: 0, linked: 0, total: parsed.people.length,
        stopped: { phase: 'creating', message: e?.message || 'The import failed.' },
      });
      track('gedcom_import_completed', {
        people_imported: 0,
        people_in_file: parsed.people.length,
        duration_ms: Date.now() - startedAt,
        stopped: 'failed',
      });
    }
    setStage('done');
  }, [parsed, bulkCreatePeople, batchUpdatePeople]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    readFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={header}>
          <h3 style={title}>Import GEDCOM</h3>
          {stage !== 'running' && (
            <button onClick={onClose} style={iconButton}><Icons.Close /></button>
          )}
        </div>

        <div style={body}>
          {stage === 'choose' && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragging ? '#C4975A' : '#D5CAB8'}`,
                  background: dragging ? '#F5EDE3' : '#fff',
                  borderRadius: 12, padding: '36px 24px', textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <div style={{ color: '#C4975A', marginBottom: 10 }}><Icons.Upload size={26} /></div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#2C2418' }}>
                  Choose a .ged file, or drop one here
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8B7D6B' }}>
                  Exported from Ancestry, MyHeritage, Gramps or any genealogy program
                </p>
                <input
                  ref={inputRef} type="file" accept=".ged,.gedcom,text/plain"
                  style={{ display: 'none' }}
                  onChange={(e) => readFile(e.target.files?.[0])}
                />
              </div>
              <p style={note}>
                Everyone in the file is added to this tree. Nothing already here is
                changed, and people are not matched against those you already have —
                importing the same file twice gives you two of everyone.
              </p>
              {error && <div style={errorBox}>{error}</div>}
            </>
          )}

          {stage === 'preview' && (
            <>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B5E4F' }}>
                <strong style={{ color: '#2C2418' }}>{fileName}</strong>
              </p>

              <div style={statRow}>
                <Stat value={parsed.stats.people} label={parsed.stats.people === 1 ? 'person' : 'people'} />
                <Stat value={parsed.stats.families} label={parsed.stats.families === 1 ? 'family' : 'families'} />
              </div>

              {parsed.stats.unnamed > 0 && (
                <p style={note}>
                  {parsed.stats.unnamed} {parsed.stats.unnamed === 1 ? 'person has' : 'people have'} no
                  name in the file and will be added as “Unknown”.
                </p>
              )}

              {tooManyPeople ? (
                <div style={errorBox}>
                  This file has {parsed.people.length} people and there is room for {room} more
                  in this tree ({peopleCount} of {maxPeople} used). Upgrade the plan or
                  remove people before importing.
                </div>
              ) : (
                <p style={note}>
                  Your tree will go from {peopleCount} to {peopleCount + parsed.people.length} people,
                  against a limit of {maxPeople}.
                </p>
              )}

              {parsed.warnings.length > 0 && (
                <details style={warningBox}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#8A6D1F' }}>
                    {parsed.warnings.length} {parsed.warnings.length === 1 ? 'thing' : 'things'} the
                    import cannot carry over
                  </summary>
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: '#6B5E4F', lineHeight: 1.6 }}>
                    {parsed.warnings.map((warning, i) => <li key={i}>{warning}</li>)}
                  </ul>
                </details>
              )}

              <p style={{ ...note, marginTop: 14 }}>
                Photos and documents are not part of a GEDCOM file, so none are
                imported. Names, dates, places, occupations, parents, children and
                marriages are.
              </p>
            </>
          )}

          {stage === 'running' && (
            <div style={{ padding: '10px 0' }}>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: '#2C2418', fontWeight: 600 }}>
                {progress.phase === 'creating' ? 'Adding people…' : 'Connecting families…'}
              </p>
              <div style={{ height: 8, background: '#E8E0D4', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                  background: '#C4975A', transition: 'width 0.2s',
                }} />
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#8B7D6B' }}>
                {progress.done} of {progress.total}
              </p>
              <p style={{ ...note, marginTop: 16 }}>
                Leave this open until it finishes — closing the tab partway will
                leave the tree half-imported.
              </p>
            </div>
          )}

          {stage === 'done' && <Summary result={result} />}
        </div>

        <div style={footer}>
          {stage === 'preview' && (
            <>
              <button onClick={() => { setStage('choose'); setParsed(null); }} style={secondaryButton}>
                Choose another file
              </button>
              <button onClick={start} disabled={tooManyPeople} style={primaryButton(tooManyPeople)}>
                Import {parsed.people.length} {parsed.people.length === 1 ? 'person' : 'people'}
              </button>
            </>
          )}
          {stage === 'choose' && <button onClick={onClose} style={secondaryButton}>Cancel</button>}
          {stage === 'done' && <button onClick={onClose} style={primaryButton(false)}>Done</button>}
        </div>
      </div>
    </div>
  );
}

/**
 * What actually happened, including when that is "most of it".
 *
 * A partial import is a real outcome of the bulk endpoint — each chunk is its
 * own transaction, so hitting the tree's limit lands the earlier ones — and the
 * number that landed is the thing the reader needs. Rounding it to "import
 * failed" is what sends someone re-importing people who are already there.
 */
function Summary({ result }) {
  const complete = !result.stopped && result.created === result.total;

  if (complete) {
    return (
      <div>
        <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#2C2418' }}>
          Imported {result.created} {result.created === 1 ? 'person' : 'people'}.
        </p>
        <p style={{ margin: 0, fontSize: 13, color: '#6B5E4F' }}>
          They are in your tree now. It may take a moment for everyone to appear on
          the canvas.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#2C2418' }}>
        {result.created > 0
          ? `Imported ${result.created} of ${result.total} people.`
          : 'Nothing was imported.'}
      </p>
      {result.stopped && (
        <div style={errorBox}>
          {result.stopped.phase === 'linking'
            ? `The people were added, but connecting their families stopped partway: ${result.stopped.message}`
            : result.stopped.message}
        </div>
      )}
      {result.created > 0 && (
        <p style={note}>
          The people who were added are in your tree — importing the file again would
          add them a second time. Remove them first if you want to start over.
        </p>
      )}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div style={{ flex: 1, background: '#F5EDE3', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#2C2418', fontFamily: "'Crimson Pro', Georgia, serif" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: '#8B7D6B' }}>{label}</div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 100,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)', padding: 16,
};

const modal = {
  width: 560, maxWidth: '100%', maxHeight: '88vh', background: '#FDFBF7',
  borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
  fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column',
};

const header = {
  padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#F5EDE3', flexShrink: 0,
};

const title = {
  margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif",
  color: '#2C2418', fontWeight: 700,
};

const body = { padding: 24, overflowY: 'auto' };

const footer = {
  padding: '16px 24px', borderTop: '1px solid #E8E0D4',
  display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
};

const iconButton = { background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B' };

const statRow = { display: 'flex', gap: 12, marginBottom: 14 };

const note = { margin: '12px 0 0', fontSize: 12, color: '#8B7D6B', lineHeight: 1.6 };

const errorBox = {
  marginTop: 14, padding: '12px 14px', borderRadius: 10,
  background: '#FDE8E8', border: '1px solid #F5C6C6',
  fontSize: 13, color: '#8B3A3A', lineHeight: 1.5,
};

const warningBox = {
  marginTop: 14, padding: '12px 14px', borderRadius: 10,
  background: '#FFF8E1', border: '1px solid #EFD9A0',
};

const secondaryButton = {
  padding: '10px 24px', borderRadius: 8, border: '1.5px solid #D5CAB8',
  background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
};

const primaryButton = (disabled) => ({
  padding: '10px 28px', borderRadius: 8, border: 'none',
  background: disabled ? '#B8AFA0' : '#3B2F1E', color: '#F5EDE3',
  fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
  fontFamily: "'DM Sans', sans-serif",
});
